import * as L from 'leaflet';
import { MapLayer } from '../interfaces/map';
import { RouteSheetSize } from '../constants/constants';

const TapReachPx = 22;
const TapTiePx = 2;

/** at phone width a tap picks the nearest route line within reach, desktop keeps the default hit test */
export function createRouteLineCanvas(
  options: L.RendererOptions,
  getOpenRouteNetworkId: () => number | undefined,
): L.Canvas {
  const defaultOnClick = (L.Canvas.prototype as any)._onClick;

  const RouteLineCanvas = L.Canvas.extend({
    _onClick(this: any, e: MouseEvent) {
      const isPhone = window.matchMedia(`(max-width: ${RouteSheetSize.phoneMaxWidth}px)`).matches;
      if (e.type !== 'click' || !isPhone) return defaultOnClick.call(this, e);

      const map: L.Map = this._map;
      const point = map.mouseEventToLayerPoint(e);
      const openRouteNetworkId = getOpenRouteNetworkId();
      let nearest: { layer: L.Polyline; distance: number } | undefined;
      let nearestOpen: { layer: L.Polyline; distance: number } | undefined;

      for (let order = this._drawFirst; order; order = order.next) {
        const layer = order.layer as L.Polyline & MapLayer;
        const routeNetworkId = layer.extraData?.routeNetworkId;
        if (!layer.options.interactive || routeNetworkId === undefined) continue;

        const distance = distanceToLine(map, layer, point);
        if (distance > TapReachPx) continue;

        if (!nearest || distance < nearest.distance) nearest = { layer, distance };
        if (
          routeNetworkId === openRouteNetworkId &&
          (!nearestOpen || distance < nearestOpen.distance)
        ) {
          nearestOpen = { layer, distance };
        }
      }

      if (!nearest) return defaultOnClick.call(this, e);

      const tapped =
        nearestOpen && nearestOpen.distance - nearest.distance <= TapTiePx ? nearestOpen : nearest;
      if ((map as any)._draggableMoved(tapped.layer)) return;

      this._fireEvent([tapped.layer], e);
    },
  });

  return new (RouteLineCanvas as new (options: L.RendererOptions) => L.Canvas)(options);
}

function distanceToLine(map: L.Map, polyline: L.Polyline, point: L.Point): number {
  const points = (polyline.getLatLngs() as L.LatLng[]).map((latLng) =>
    map.latLngToLayerPoint(latLng),
  );
  let distance = Infinity;
  for (let i = 0; i < points.length - 1; i++) {
    distance = Math.min(
      distance,
      L.LineUtil.pointToSegmentDistance(point, points[i], points[i + 1]),
    );
  }
  return distance;
}
